import * as React from "react";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import {
    ClearWorkflowNavigationLogs,
    GetWorkflowNavigationLogs,
} from "source/app/services/Workflows/WorkflowNavigationDiagnostics";
import {
    ClearClientDiagnosticLogs,
    GetClientErrorDetails,
    GetClientDiagnosticReportText,
    GetClientDiagnosticLogs,
    LogClientDiagnostic,
} from "source/app/services/ClientDiagnostics";
import { SendClientLogsReportAsync } from "source/app/presentation/Shared/ErrorBoundary/SendClientErrorReport";
import ChevronPanel from "../../Shared/ChevronPanel/ChevronPanel";
import { StyleClassAuto } from "../../Shared/Styles/Auto/StyleClassAuto";
import { ERPGIcon } from "motw_core/UI/Icons/ERPGIcon";

type ClientLogsEmailStatus = "idle" | "sending" | "sent" | "failed";

export function ClientLogs() {
    const [logsText, setLogsText] = React.useState(GetClientLogsText());
    const [emailStatus, setEmailStatus] =
        React.useState<ClientLogsEmailStatus>("idle");
    const [emailIncidentId, setEmailIncidentId] = React.useState<string>();

    const refreshLogs = () => setLogsText(GetClientLogsText());
    const emailLogs = () => {
        if (emailStatus === "sending") {
            return;
        }

        setEmailStatus("sending");
        setEmailIncidentId(undefined);
        LogClientDiagnostic("client-logs", "email-request", {
            recordCount: GetClientLogRecords().length,
        });

        void SendClientLogsReportAsync(GetClientDiagnosticReportText())
            .then((incidentId) => {
                setEmailStatus("sent");
                setEmailIncidentId(incidentId);
                LogClientDiagnostic("client-logs", "email-sent", {
                    incidentId,
                });
            })
            .catch((error: unknown) => {
                setEmailStatus("failed");
                LogClientDiagnostic(
                    "client-logs",
                    "email-error",
                    { error: GetClientErrorDetails(error) },
                    "error"
                );
            });
    };
    const copyLogs = () => void navigator.clipboard.writeText(logsText);
    const exportLogs = () => {
        const blob = new Blob([logsText], {
            type: "application/json;charset=utf-8",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = `motw-client-logs-${new Date()
            .toISOString()
            .replace(/[:.]/g, "-")}.json`;
        link.style.display = "none";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
    };

    return (
        <ChevronPanel
            id="ClientLogs"
            label="Client logs"
            icon={ERPGIcon["scroll-unfurled"]}
            dynamicOpacity={false}
            withClasses={StyleClassAuto({ textAlign: "center" })}
        >
            <div
                className={StyleClassAuto({
                    background: `rgba(0, 0, 0, 0.2)`,
                    border: `1px solid rgba(255, 255, 255, 0.1)`,
                    borderRadius: `8px`,
                    color: `#f2ead7`,
                    marginTop: `8px`,
                    padding: `10px`,
                })}
            >
                <div
                    className={StyleClassAuto({
                        fontSize: `12px`,
                        marginBottom: `8px`,
                        opacity: 0.92,
                    })}
                >
                    <Hyperlink whenClicked={refreshLogs}>refresh logs</Hyperlink>
                    <span> | </span>
                    <Hyperlink
                        whenClicked={() => {
                            ClearWorkflowNavigationLogs();
                            ClearClientDiagnosticLogs();
                            refreshLogs();
                        }}
                    >
                        clear logs
                    </Hyperlink>
                    <span> | </span>
                    <Hyperlink whenClicked={copyLogs}>copy to clipboard</Hyperlink>
                    <span> | </span>
                    <Hyperlink whenClicked={exportLogs}>export to file</Hyperlink>
                    <span> | </span>
                    <Hyperlink whenClicked={emailLogs}>email logs</Hyperlink>
                    {emailStatus === "sending" && <span> (sending...)</span>}
                    {emailStatus === "sent" && (
                        <span> (sent{emailIncidentId ? ` ${emailIncidentId}` : ""})</span>
                    )}
                    {emailStatus === "failed" && <span> (failed)</span>}
                </div>
                <textarea
                    readOnly
                    value={logsText}
                    spellCheck={false}
                    className={StyleClassAuto({
                        background: `rgba(0, 0, 0, 0.38)`,
                        border: `1px solid rgba(255, 255, 255, 0.08)`,
                        borderRadius: `6px`,
                        boxSizing: `border-box`,
                        color: `#f4ecd8`,
                        display: `block`,
                        fontFamily: `monospace`,
                        fontSize: `10px`,
                        height: `260px`,
                        lineHeight: `1.35`,
                        maxWidth: `100%`,
                        padding: `8px`,
                        resize: `vertical`,
                        width: `100%`,
                        whiteSpace: `pre`,
                    })}
                />
            </div>
        </ChevronPanel>
    );
}

function GetClientLogsText() {
    return JSON.stringify(GetClientLogRecords(), null, 2);
}

function GetClientLogRecords() {
    return [
        ...(GetWorkflowNavigationLogs() as Array<{ timestamp: string }>),
        ...GetClientDiagnosticLogs(),
    ].sort((left, right) =>
        left.timestamp.localeCompare(right.timestamp)
    );
}
